<template>
    <div class="modal-overlay" @click.self="$store.commit('dailyPosReport', false)">
        <div class="modal-container">
            <div class="modal-header">
                <div class="header-title">
                    <i class="bi bi-calendar-check"></i>
                    <h5>Daily Position Report</h5>
                </div>
                <button type="button" class="btn-close" @click="$store.commit('dailyPosReport', false)" aria-label="Close"></button>
            </div>

            <div class="modal-body" v-if="data">
                <!-- Total P&L Card -->
                <div class="total-pl-card" :class="posData.pl >= 0 ? 'positive' : 'negative'">
                    <span class="pl-label">Total P&L</span>
                    <span class="pl-value">{{ posData.pl >= 0 ? '+' : '' }}{{ posData.pl.toLocaleString('en-IN') }} &#8377;</span>
                </div>

                <!-- Position Count -->
                <div class="section-header">
                    <span class="section-title">Positions</span>
                    <span class="position-count">{{ data.length }} trades</span>
                </div>

                <!-- Desktop Table -->
                <div class="data-table d-none d-md-block">
                    <div class="table-header">
                        <div class="col-product">Product</div>
                        <div class="col-instrument">Instrument</div>
                        <div class="col-qty">Qty</div>
                        <div class="col-buy">Avg Buy</div>
                        <div class="col-sell">Avg Sell</div>
                        <div class="col-pl">P&L</div>
                    </div>
                    <div class="table-row" v-for="(item, index) in data" :key="index">
                        <div class="col-product"><span class="product-badge">{{ item.duration }}</span></div>
                        <div class="col-instrument">{{ item.instrumentName }}</div>
                        <div class="col-qty">{{ item.qty }}</div>
                        <div class="col-buy">{{ item.avgBuyPrice.toFixed(2) }}</div>
                        <div class="col-sell">{{ item.avgSellPrice.toFixed(2) }}</div>
                        <div class="col-pl" :class="item.pl >= 0 ? 'positive' : 'negative'">{{ getPlSign(item) }}</div>
                    </div>
                </div>

                <!-- Mobile Cards -->
                <div class="mobile-cards d-block d-md-none">
                    <div class="position-card" v-for="(item, index) in data" :key="index">
                        <div class="card-header">
                            <span class="instrument-name">{{ item.instrumentName }}</span>
                            <span class="product-badge">{{ item.duration }}</span>
                        </div>
                        <div class="card-details">
                            <div class="detail-item">
                                <span class="detail-label">Qty</span>
                                <span class="detail-value">{{ item.qty }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Avg Buy</span>
                                <span class="detail-value">{{ item.avgBuyPrice.toFixed(2) }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Avg Sell</span>
                                <span class="detail-value">{{ item.avgSellPrice.toFixed(2) }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">P&L</span>
                                <span class="detail-value" :class="item.pl >= 0 ? 'positive' : 'negative'">{{ getPlSign(item) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="modal-body loading" v-else>
                <div class="loading-spinner">
                    <i class="bi bi-arrow-repeat"></i>
                    <span>Loading positions...</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getPosReport } from '@/service/restService'
export default {
  name: 'DailyPosReport',
  props: {
    posData: {
      type: Object,
      required: true
    }
  },
  components: {
  },
  data () {
    return {
      data: null
    }
  },
  created () {
    getPosReport(this.posData.date).then((response) => {
      this.data = response.data
    }).catch((error) => {
      this.$store.commit('addToast', {
        title: 'Failure!',
        type: 'error',
        message: error.response.data.message[0]
      })
    })
  },
  methods: {
    getPlSign: function (item) {
      var res = item.pl
      return res > 0 ? '+' + res.toLocaleString('en-IN') : res.toLocaleString('en-IN')
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-container {
  background-color: var(--bs-body-bg);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 900px;
  width: 100%;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--bs-border-color);
  flex-shrink: 0;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-title i {
  font-size: 24px;
  color: var(--bs-primary);
}

.header-title h5 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--bs-body-color);
}

.modal-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-body.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: var(--bs-secondary-color);
}

.loading-spinner i {
  font-size: 32px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Total P&L Card */
.total-pl-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
}

.total-pl-card.positive {
  background: rgba(11, 218, 81, 0.1);
  border: 1px solid rgba(11, 218, 81, 0.2);
}

.total-pl-card.negative {
  background: rgba(239, 1, 7, 0.1);
  border: 1px solid rgba(239, 1, 7, 0.2);
}

.pl-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--bs-secondary-color);
}

.pl-value {
  font-size: 20px;
  font-weight: 700;
}

.total-pl-card.positive .pl-value {
  color: #0BDA51;
}

.total-pl-card.negative .pl-value {
  color: #EF0107;
}

/* Section Header */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--bs-body-color);
}

.position-count {
  font-size: 12px;
  color: var(--bs-secondary-color);
  background: var(--bs-tertiary-bg);
  padding: 4px 10px;
  border-radius: 20px;
}

/* Desktop Table */
.data-table {
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 80px 2fr 60px 90px 90px 100px;
  padding: 12px 16px;
  background: var(--bs-tertiary-bg);
  font-size: 11px;
  font-weight: 600;
  color: var(--bs-secondary-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-row {
  display: grid;
  grid-template-columns: 80px 2fr 60px 90px 90px 100px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--bs-border-color);
  font-size: 13px;
  align-items: center;
}

.table-row:last-child {
  border-bottom: none;
}

.table-row:hover {
  background: var(--bs-tertiary-bg);
}

.product-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  background: var(--bs-tertiary-bg);
  color: var(--bs-secondary-color);
}

.col-instrument {
  font-weight: 500;
  color: var(--bs-body-color);
}

.col-pl {
  font-weight: 600;
}

.positive {
  color: #0BDA51;
}

.negative {
  color: #EF0107;
}

/* Mobile Cards */
.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.position-card {
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  padding: 14px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--bs-border-color);
}

.instrument-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--bs-body-color);
}

.card-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 11px;
  color: var(--bs-secondary-color);
}

.detail-value {
  font-size: 13px;
  font-weight: 600;
  color: var(--bs-body-color);
}

.detail-value.positive {
  color: #0BDA51;
}

.detail-value.negative {
  color: #EF0107;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }

  .modal-container {
    max-height: 90vh;
    border-radius: 12px;
  }

  .modal-header {
    padding: 16px;
  }

  .header-title h5 {
    font-size: 16px;
  }

  .modal-body {
    padding: 16px;
  }

  .total-pl-card {
    padding: 14px 16px;
  }

  .pl-value {
    font-size: 18px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/report/DailyPosReport.vue